import { getCapabilitiesUrl } from './wms'

/**
 * The `TIME` dimension of a WMS layer, read from its workspace's
 * GetCapabilities document.
 *
 * One capabilities fetch covers every layer in a workspace, so the parsed
 * result is cached per base URL. nowCOAST's radar dimension carried 125
 * instants over 8 h 20 m at ~4 min cadence when probed (2026-09-16), with
 * `nearestValue="1"`, so an inexact TIME snaps server-side rather than
 * erroring.
 *
 * Parsed with DOMParser rather than a regex: the documents run 30-450 KB and
 * the layer list is nested several levels deep.
 */

/** Capabilities are cheap to re-read and radar gains a frame every 4 minutes. */
const TTL_MS = 4 * 60 * 1000

interface CacheEntry {
  at: number
  layers: Promise<Map<string, Date[]>>
}

const cache = new Map<string, CacheEntry>()

/** Which end of the axis a layer's frame budget is taken from. */
export type FrameMode = 'recent' | 'upcoming'

export function parseCapabilitiesTimes(xml: string): Map<string, Date[]> {
  const out = new Map<string, Date[]>()
  const doc = new DOMParser().parseFromString(xml, 'text/xml')
  if (doc.querySelector('parsererror')) return out

  for (const el of Array.from(doc.getElementsByTagName('Layer'))) {
    // `> Name` only: a container Layer's Name must not pick up a child's.
    const name = Array.from(el.children).find((c) => c.tagName === 'Name')?.textContent?.trim()
    if (!name) continue
    const dim = Array.from(el.children).find(
      (c) => c.tagName === 'Dimension' && c.getAttribute('name')?.toLowerCase() === 'time',
    )
    if (!dim) continue
    const times = (dim.textContent ?? '')
      .split(',')
      .map((s) => new Date(s.trim()))
      .filter((d) => !Number.isNaN(d.getTime()))
    times.sort((a, b) => a.getTime() - b.getTime())
    if (times.length) out.set(name, times)
  }
  return out
}

async function loadWorkspace(base: string): Promise<Map<string, Date[]>> {
  const resp = await fetch(getCapabilitiesUrl(base))
  if (!resp.ok) throw new Error(`GetCapabilities ${resp.status}`)
  return parseCapabilitiesTimes(await resp.text())
}

/**
 * Every instant the layer publishes, oldest first. An empty array means the
 * layer has no time dimension, is absent from the document, or the fetch
 * failed — all three degrade to "draw the server's default frame".
 */
export async function fetchTimes(base: string, layerName: string): Promise<Date[]> {
  const now = Date.now()
  let entry = cache.get(base)
  if (!entry || now - entry.at > TTL_MS) {
    entry = { at: now, layers: loadWorkspace(base) }
    cache.set(base, entry)
  }
  try {
    const layers = await entry.layers
    // The document names layers unqualified (`conus_air_temperature`), while
    // GetMap wants them workspace-qualified. Accept either spelling.
    return layers.get(layerName) ?? layers.get(layerName.split(':').pop() ?? '') ?? []
  } catch {
    // A failed capabilities fetch must not be remembered for four minutes.
    if (cache.get(base) === entry) cache.delete(base)
    return []
  }
}

/**
 * Trim a full dimension to the frames we will actually draw.
 *
 * Observed layers (radar, satellite) run backwards from now, so the newest
 * frames are the interesting ones. Forecast layers (NDFD) run forwards, so the
 * soonest are. Every GetMap is a live render at NOAA's end (no tile cache), so
 * the cap is a courtesy as much as a memory budget.
 */
export function pickFrames(times: readonly Date[], count: number, mode: FrameMode): Date[] {
  if (times.length <= count) return [...times]
  return mode === 'upcoming' ? times.slice(0, count) : times.slice(times.length - count)
}

/** Index of the frame closest to `t`. -1 for an empty axis. */
export function nearestIndex(times: readonly Date[], t: Date): number {
  if (times.length === 0) return -1
  let best = 0
  let bestDelta = Infinity
  for (let i = 0; i < times.length; i++) {
    const delta = Math.abs(times[i].getTime() - t.getTime())
    if (delta < bestDelta) {
      bestDelta = delta
      best = i
    }
  }
  return best
}

/** Tests only: the module-level capabilities cache outlives a component. */
export function _resetTimeCache(): void {
  cache.clear()
}
